import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Brain, 
  Target, 
  Languages, 
  TrendingUp, 
  CheckCircle, 
  AlertCircle,
  Sparkles,
  Eye,
  Copy
} from 'lucide-react';
import { useAITextRecognition, type TextRegion, type TextAnalysisResult } from '@/hooks/useAITextRecognition';
import { useSmartFieldSuggestions, type SmartSuggestion, type SuggestionContext } from '@/hooks/useSmartFieldSuggestions';

interface AITextRecognitionPanelProps {
  selectedText?: string;
  onFieldSuggestion?: (fieldName: string, text: string) => void;
  availableFields?: { name: string; displayName: string; type: string; required: boolean }[];
  documentContent?: string;
}

const AITextRecognitionPanel: React.FC<AITextRecognitionPanelProps> = ({
  selectedText,
  onFieldSuggestion,
  availableFields = [],
  documentContent
}) => {
  const [currentAnalysis, setCurrentAnalysis] = useState<TextAnalysisResult | null>(null);
  const [detectedRegions, setDetectedRegions] = useState<TextRegion[]>([]);
  const [showRegionAnalysis, setShowRegionAnalysis] = useState(false);
  
  const { 
    isAnalyzing, 
    analyzeText, 
    detectTextRegions, 
    getQualityScore,
    analysisHistory 
  } = useAITextRecognition();
  
  const {
    suggestions,
    isGenerating,
    generateSuggestions,
    applySuggestion,
    getSuggestionQuality
  } = useSmartFieldSuggestions();

  // Analyze selected text when it changes
  useEffect(() => {
    if (selectedText && selectedText.trim().length > 0) {
      analyzeText(selectedText)
        .then(result => {
          setCurrentAnalysis(result);
          
          // Generate smart suggestions
          const context: SuggestionContext = {
            documentType: 'utility_bill',
            availableFields: availableFields.map(field => ({
              fieldName: field.name,
              displayName: field.displayName,
              fieldType: field.type as any,
              isRequired: field.required,
              priority: field.required ? 10 : 5
            }))
          };
          
          generateSuggestions(selectedText, context);
        })
        .catch(error => {
          console.error('Analysis error:', error);
        });
    }
  }, [selectedText, analyzeText, generateSuggestions, availableFields]);

  // Analyze document regions
  const handleRegionAnalysis = async () => {
    if (!documentContent) return;
    
    setShowRegionAnalysis(true);
    try {
      const regions = await detectTextRegions(documentContent, {
        minConfidence: 0.4,
        categories: ['amount', 'date', 'supplier_name', 'account_number']
      });
      setDetectedRegions(regions);
    } catch (error) {
      console.error('Region detection error:', error);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      amount: 'bg-green-500',
      date: 'bg-blue-500',
      supplier_name: 'bg-purple-500',
      supplier_address: 'bg-purple-400',
      account_number: 'bg-orange-500',
      reference_number: 'bg-orange-400',
      customer_name: 'bg-cyan-500',
      customer_address: 'bg-cyan-400',
      service_type: 'bg-indigo-500',
      meter_reading: 'bg-yellow-500',
      unknown: 'bg-gray-400'
    };
    return colors[category] || 'bg-gray-400';
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const qualityScore = selectedText ? getQualityScore(selectedText) : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Brain className="h-5 w-5" />
            AI Text Recognition
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="analysis" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="analysis" className="flex items-center gap-1">
                <Target className="h-4 w-4" />
                Analysis
              </TabsTrigger>
              <TabsTrigger value="suggestions" className="flex items-center gap-1">
                <Sparkles className="h-4 w-4" />
                Suggestions
              </TabsTrigger>
              <TabsTrigger value="regions" className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                Regions
              </TabsTrigger>
            </TabsList>

            <TabsContent value="analysis" className="space-y-4">
              {selectedText ? (
                <>
                  {/* Selected Text Display */}
                  <div className="p-3 border rounded-md bg-muted/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Copy className="h-4 w-4" />
                      <span className="text-sm font-medium">Selected Text:</span>
                    </div>
                    <p className="text-sm font-mono bg-background p-2 rounded border">
                      "{selectedText}"
                    </p>
                  </div>

                  {/* Quality Score */}
                  {qualityScore && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Quality Score</span>
                        <span className={`text-sm font-medium ${getConfidenceColor(qualityScore.score)}`}>
                          {Math.round(qualityScore.score * 100)}%
                        </span>
                      </div>
                      <Progress value={qualityScore.score * 100} className="h-2" />
                      {qualityScore.recommendations.length > 0 && (
                        <Alert>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            {qualityScore.recommendations[0]}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}

                  {/* Analysis Results */}
                  {currentAnalysis && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Category:</span>
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${getCategoryColor(currentAnalysis.category)}`} />
                          {currentAnalysis.category.replace('_', ' ')}
                        </Badge>
                        <Badge variant="outline" className={getConfidenceColor(currentAnalysis.confidence)}>
                          {Math.round(currentAnalysis.confidence * 100)}%
                        </Badge>
                      </div>

                      <div className="flex items-center gap-2">
                        <Languages className="h-4 w-4" />
                        <span className="text-sm">Language: {currentAnalysis.language.toUpperCase()}</span>
                      </div>

                      {/* Metadata */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {currentAnalysis.metadata.isNumeric && (
                          <Badge variant="outline" className="justify-center">Numeric</Badge>
                        )}
                        {currentAnalysis.metadata.isDate && (
                          <Badge variant="outline" className="justify-center">Date</Badge>
                        )}
                        {currentAnalysis.metadata.isCurrency && (
                          <Badge variant="outline" className="justify-center">Currency</Badge>
                        )}
                        {currentAnalysis.metadata.hasEmail && (
                          <Badge variant="outline" className="justify-center">Email</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {isAnalyzing && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      Analyzing text...
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Brain className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Select text from the document to analyze</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="suggestions" className="space-y-4">
              {suggestions.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <TrendingUp className="h-4 w-4" />
                    Smart Field Suggestions
                  </div>
                  
                  {suggestions.map((suggestion) => {
                    const quality = getSuggestionQuality(suggestion.id);
                    return (
                      <Card key={suggestion.id} className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{suggestion.fieldName}</span>
                            <Badge variant="outline" className={getConfidenceColor(suggestion.confidence)}>
                              {Math.round(suggestion.confidence * 100)}%
                            </Badge>
                            {quality && (
                              <Badge variant="secondary" className="text-xs">
                                {quality.label}
                              </Badge>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => {
                              if (process.env.NODE_ENV === 'development') {
                                console.log('Applying suggestion:', suggestion);
                              }
                              applySuggestion(suggestion.id, (fieldName, text) => {
                                if (process.env.NODE_ENV === 'development') {
                                  console.log('Callback triggered:', fieldName, text);
                                }
                                onFieldSuggestion?.(fieldName, text);
                              });
                            }}
                            className="flex items-center gap-1"
                            disabled={suggestion.applied}
                          >
                            <CheckCircle className="h-3 w-3" />
                            {suggestion.applied ? 'Applied' : 'Apply'}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {suggestion.reasoning}
                        </p>
                      </Card>
                    );
                  })}
                </div>
              ) : isGenerating ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  Generating suggestions...
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {selectedText ? 'No field suggestions available' : 'Select text to see field suggestions'}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="regions" className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Eye className="h-4 w-4" />
                  Document Regions
                </div>
                {documentContent && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRegionAnalysis}
                    disabled={isAnalyzing}
                  >
                    Analyze Document
                  </Button>
                )}
              </div>

              {detectedRegions.length > 0 ? (
                <div className="space-y-2">
                  {detectedRegions.map((region) => (
                    <Card key={region.id} className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-2 h-2 rounded-full ${getCategoryColor(region.category)}`} />
                        <span className="text-sm font-medium capitalize">
                          {region.category.replace('_', ' ')}
                        </span>
                        <Badge variant="outline" className={getConfidenceColor(region.confidence)}>
                          {Math.round(region.confidence * 100)}%
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">
                        "{region.text}"
                      </p>
                    </Card>
                  ))}
                </div>
              ) : showRegionAnalysis ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No high-confidence regions detected</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Analyze document to detect text regions</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AITextRecognitionPanel;